import React from 'react';
import { FileText, Image as ImageIcon, Video, ExternalLink, Phone } from 'lucide-react';
import { TemplateComponent } from '../../../types/models/whatsapp';

interface TemplatePreviewProps {
  components: TemplateComponent[];
  /** Example values keyed by position, used to render {{1}} as real text. */
  examples?: string[];
}

/**
 * WhatsApp-styled preview of a template as the recipient will see it.
 *
 * Substituting the example values matters: "{{1}}, your trial is on {{2}}"
 * reads as valid until you see "Asha, your trial is on Saturday" and notice
 * the missing comma.
 */
const TemplatePreview: React.FC<TemplatePreviewProps> = ({
  components,
  examples = []
}) => {
  const header = components.find((c) => c.type === 'HEADER');
  const body = components.find((c) => c.type === 'BODY');
  const footer = components.find((c) => c.type === 'FOOTER');
  const buttons = components.find((c) => c.type === 'BUTTONS')?.buttons || [];

  const substitute = (text: string): string =>
    text.replace(/\{\{(\d+)\}\}/g, (match, position) => {
      const value = examples[Number(position) - 1];
      return value ? value : match;
    });

  return (
    <div className="rounded-lg bg-wa-chatBg p-4">
      <div className="max-w-[20rem] rounded-lg bg-wa-bubbleOut p-2 shadow-sm">
        {header && (
          <div className="mb-1.5">
            {header.format === 'TEXT' && header.text && (
              <p className="text-[15px] font-semibold leading-snug text-gray-900">
                {substitute(header.text)}
              </p>
            )}
            {header.format === 'IMAGE' && (
              <div className="flex h-32 items-center justify-center rounded bg-gray-300">
                <ImageIcon className="h-8 w-8 text-gray-500" />
              </div>
            )}
            {header.format === 'VIDEO' && (
              <div className="flex h-32 items-center justify-center rounded bg-gray-700">
                <Video className="h-8 w-8 text-gray-300" />
              </div>
            )}
            {header.format === 'DOCUMENT' && (
              <div className="flex items-center gap-2 rounded bg-black/5 p-2">
                <FileText className="h-6 w-6 shrink-0 text-primary-600" />
                <span className="text-xs text-gray-600">Document</span>
              </div>
            )}
          </div>
        )}

        <p className="whitespace-pre-wrap break-words text-[15px] leading-snug text-gray-900">
          {body?.text ? substitute(body.text) : (
            <span className="italic text-gray-400">Message body…</span>
          )}
        </p>

        {footer?.text && (
          <p className="mt-1.5 text-[13px] text-gray-500">{footer.text}</p>
        )}

        <p className="mt-1 text-right text-[10px] text-gray-500">12:00</p>
      </div>

      {buttons.length > 0 && (
        <div className="mt-1 max-w-[20rem] space-y-1">
          {buttons.map((button, index) => (
            <div
              key={`${button.type}-${index}`}
              className="flex items-center justify-center gap-1.5 rounded-lg bg-wa-bubbleOut px-3 py-2 text-[14px] font-medium text-primary-600 shadow-sm"
            >
              {button.type === 'URL' && <ExternalLink className="h-3.5 w-3.5" />}
              {button.type === 'PHONE_NUMBER' && <Phone className="h-3.5 w-3.5" />}
              {button.text || 'Button'}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default TemplatePreview;
